import React, { useCallback, useState } from 'react';
import { X, CreditCard, ArrowLeft, ChevronRight } from 'lucide-react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import toast from 'react-hot-toast';
import { GiftCardFormData } from '../types';
import apiService from '../services/api';

export type CustomerGiftCardPurchaseContext =
  | { type: 'store'; businessId: string; businessName: string }
  | { type: 'universal' };

function PurchasePaymentForm(props: {
  context: CustomerGiftCardPurchaseContext;
  amountLabel: string;
  onBack: () => void;
  onPaid: () => void;
}) {
  const { context, amountLabel, onBack, onPaid } = props;
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      if (!stripe || !elements) return;

      try {
        setSubmitting(true);
        const result = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: window.location.href },
          redirect: 'if_required',
        });

        if (result.error) {
          setError(result.error.message || 'Payment failed. Please try again.');
          return;
        }

        const pi = result.paymentIntent;
        if (!pi?.id) {
          setError('Payment could not be confirmed. Please try again.');
          return;
        }

        const sync =
          context.type === 'store'
            ? await apiService.syncGiftCardPurchasePaymentIntent(context.businessId, pi.id)
            : await apiService.syncUniversalGiftCardPurchasePaymentIntent(pi.id);

        if (sync.mappedStatus !== 'SUCCEEDED' || !sync.giftCardId) {
          setError('Payment is still processing. Please wait a moment and refresh your gift cards list.');
          return;
        }

        toast.success('Gift card purchased successfully');
        onPaid();
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || 'Payment failed. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [context, elements, onPaid, stripe]
  );

  const cardLabel = context.type === 'store' ? context.businessName : 'AACore (use anywhere)';

  return (
    <div className="space-y-4">
      <div className="p-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700">
        <div className="text-xs text-gray-500 mb-1">AMOUNT TO CHARGE</div>
        <div className="text-xl font-bold text-gray-900">${amountLabel}</div>
        <p className="text-xs text-gray-500 mt-1 mb-0">{cardLabel}</p>
        <p className="text-xs text-gray-500 mt-2 mb-0">
          The gift card is created and emailed to the recipient after payment succeeds.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <PaymentElement />
        </div>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        <button
          type="submit"
          className="aacore-btn-primary w-full justify-center gap-2"
          disabled={!stripe || !elements || submitting}
        >
          <CreditCard className="w-4 h-4" />
          {submitting ? 'Processing…' : 'Pay now'}
        </button>
      </form>
      <button type="button" onClick={onBack} className="aacore-btn2-outline w-full justify-center gap-2" disabled={submitting}>
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>
    </div>
  );
}

interface CustomerGiftCardPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchased: () => void;
  stripePromise: Promise<Stripe | null> | null;
  clientSecret: string | null;
  context: CustomerGiftCardPurchaseContext | null;
  amountLabel: string;
}

const CustomerGiftCardPurchaseModal: React.FC<CustomerGiftCardPurchaseModalProps> = ({
  isOpen,
  onClose,
  onPurchased,
  stripePromise,
  clientSecret,
  context,
  amountLabel,
}) => {
  if (!isOpen || !context || !clientSecret || !stripePromise) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Complete payment</h3>
          <button type="button" onClick={onClose} className="p-1 text-gray-500 hover:text-gray-900 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PurchasePaymentForm
              context={context}
              amountLabel={amountLabel}
              onBack={onClose}
              onPaid={() => {
                onPurchased();
                onClose();
              }}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export async function startCustomerGiftCardPurchase(
  context: CustomerGiftCardPurchaseContext,
  payload: GiftCardFormData
): Promise<{ clientSecret: string; amountCents: number }> {
  if (context.type === 'store') {
    const pi = await apiService.createGiftCardPurchasePaymentIntent(context.businessId, payload);
    return { clientSecret: pi.clientSecret, amountCents: pi.amountCents };
  }
  const pi = await apiService.createUniversalGiftCardPurchasePaymentIntent(payload);
  return { clientSecret: pi.clientSecret, amountCents: pi.amountCents };
}

export default CustomerGiftCardPurchaseModal;
